var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { announce } from '../helper/announcer';
import { CUBE_SIZE, CSS_CUBE_SIZE, COLOR_HEX_TO_NAME, linkCubesViaMenu, } from './LinkingCubes';
var buildCandidates = function (manipulative, focusedCube, t) {
    var _a, _b, _c;
    var colorLabel = function (hex) {
        var key = COLOR_HEX_TO_NAME[(hex !== null && hex !== void 0 ? hex : '').toLowerCase()];
        if (!key)
            return t('text.cube');
        var mapped = key.replace(/\s+/g, '');
        var i18nKey = "text.color".concat(mapped.charAt(0).toUpperCase()).concat(mapped.slice(1));
        var translated = t(i18nKey);
        return translated === i18nKey ? key : translated;
    };
    var sameOrientationOthers = manipulative
        .filter(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.type) === 'linkingCubes' && m.data.id !== focusedCube.id; })
        .map(function (m) { return m.data; })
        .filter(function (c) {
        return c.orientation === focusedCube.orientation &&
            (!focusedCube.chainId || c.chainId !== focusedCube.chainId);
    });
    var byChain = new Map();
    for (var _i = 0, sameOrientationOthers_1 = sameOrientationOthers; _i < sameOrientationOthers_1.length; _i++) {
        var c = sameOrientationOthers_1[_i];
        var k = (_a = c.chainId) !== null && _a !== void 0 ? _a : undefined;
        if (!byChain.has(k))
            byChain.set(k, []);
        byChain.get(k).push(c);
    }
    var out = [];
    byChain.forEach(function (members, chainId) {
        var _a, _b;
        if (!chainId)
            return;
        var sorted = __spreadArray([], members, true).sort(function (a, b) { var _a, _b; return ((_a = a.chainIndex) !== null && _a !== void 0 ? _a : 0) - ((_b = b.chainIndex) !== null && _b !== void 0 ? _b : 0); });
        if (sorted.length < 2)
            return;
        var first = sorted[0];
        var last = sorted[sorted.length - 1];
        var chainColor = colorLabel(first.color);
        out.push({
            cubeId: first.id,
            color: first.color,
            center: first.center,
            scale: (_a = first.scale) !== null && _a !== void 0 ? _a : 1,
            orientation: first.orientation,
            kind: 'chainStart',
            side: 'before',
            label: t('text.linkToChainStart', { color: chainColor }),
        });
        out.push({
            cubeId: last.id,
            color: last.color,
            center: last.center,
            scale: (_b = last.scale) !== null && _b !== void 0 ? _b : 1,
            orientation: last.orientation,
            kind: 'chainEnd',
            side: 'after',
            label: t('text.linkToChainEnd', { color: chainColor }),
        });
    });
    var standalones = (_b = byChain.get(undefined)) !== null && _b !== void 0 ? _b : [];
    for (var _d = 0, standalones_1 = standalones; _d < standalones_1.length; _d++) {
        var c = standalones_1[_d];
        out.push({
            cubeId: c.id,
            color: c.color,
            center: c.center,
            scale: (_c = c.scale) !== null && _c !== void 0 ? _c : 1,
            orientation: c.orientation,
            kind: 'standalone',
            label: t('text.linkToStandaloneCube', { color: colorLabel(c.color) }),
        });
    }
    return out;
};
var LinkingCubePicker = function () {
    var _a;
    var t = useTranslation().t;
    var _b = useContext(DataContext), manipulative = _b.manipulative, setManipulative = _b.setManipulative;
    var _c = useState(null), focusedCubeId = _c[0], setFocusedCubeId = _c[1];
    var _d = useState(null), subChoice = _d[0], setSubChoice = _d[1];
    var containerRef = useRef(null);
    var firstBtnRef = useRef(null);
    var subFirstBtnRef = useRef(null);
    var returnFocusIdRef = useRef(null);
    useEffect(function () {
        var onOpen = function (data) {
            setFocusedCubeId(data.focusedCubeId);
            setSubChoice(null);
            returnFocusIdRef.current = data.focusedCubeId;
            announce(t('text.linkingPickerOpened'));
        };
        customEventHandler.addEventListener('openLinkingCubePicker', onOpen);
        return function () {
            return customEventHandler.removeEventListener('openLinkingCubePicker', onOpen);
        };
    }, [t]);
    // Auto-focus the first picker button whenever the picker opens or returns
    // from the sub-prompt to the main grid.
    // 60ms delay beats closeMenu's 50ms focus-restore timer so the picker wins focus.
    useEffect(function () {
        if (!focusedCubeId)
            return;
        var id = setTimeout(function () {
            var _a, _b;
            if (subChoice) {
                (_a = subFirstBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
            else {
                (_b = firstBtnRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }
        }, 60);
        return function () { return clearTimeout(id); };
    }, [focusedCubeId, subChoice]);
    var focusedItem = focusedCubeId
        ? manipulative.find(function (m) { var _a; return ((_a = m.data) === null || _a === void 0 ? void 0 : _a.id) === focusedCubeId; })
        : undefined;
    var focusedCube = focusedItem && focusedItem.data.type === 'linkingCubes'
        ? focusedItem.data
        : null;
    var candidates = focusedCube ? buildCandidates(manipulative, focusedCube, t) : [];
    // Sub-prompt lookup — computed before any conditional returns so hook order
    // stays stable across renders (see useEffect below).
    var sub = subChoice
        ? candidates.find(function (c) { return c.cubeId === subChoice.targetId; })
        : null;
    // If we're activated but have no valid target, tear down in an effect to
    // avoid mutating state during render.
    useEffect(function () {
        if (focusedCubeId && (!focusedCube || candidates.length === 0)) {
            setFocusedCubeId(null);
            setSubChoice(null);
        }
    }, [focusedCubeId, focusedCube, candidates.length]);
    // Same for a stale sub-choice (target disappeared while sub-prompt was open).
    useEffect(function () {
        if (subChoice && !sub)
            setSubChoice(null);
    }, [subChoice, sub]);
    // Click-outside: close picker and let the event propagate naturally.
    // Exclude the portaled sub-menu so clicks inside it don't count as outside.
    useEffect(function () {
        if (!focusedCubeId)
            return;
        var handleOutside = function (e) {
            var _a, _b;
            var target = e.target;
            if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            if ((_b = target === null || target === void 0 ? void 0 : target.closest) === null || _b === void 0 ? void 0 : _b.call(target, '.shape-context-menu--linkingCubePickerSub'))
                return;
            setFocusedCubeId(null);
            setSubChoice(null);
        };
        window.addEventListener('pointerdown', handleOutside, { capture: true });
        return function () { return window.removeEventListener('pointerdown', handleOutside, { capture: true }); };
    }, [focusedCubeId]);
    if (!focusedCubeId || !focusedCube || candidates.length === 0)
        return null;
    var restoreFocusToOrigin = function () {
        var id = returnFocusIdRef.current;
        if (!id)
            return;
        requestAnimationFrame(function () {
            var el = document.querySelector("[data-id=\"".concat(id, "\"]"));
            el === null || el === void 0 ? void 0 : el.focus();
        });
    };
    var closePicker = function () {
        setFocusedCubeId(null);
        setSubChoice(null);
    };
    var commitLink = function (targetId, side) {
        var ok = linkCubesViaMenu(focusedCubeId, targetId, side, manipulative, setManipulative);
        if (ok)
            announce(t('text.cubesLinked'));
        closePicker();
        // linkCubesViaMenu handles focusing the new chain's base internally —
        // necessary because the source cube may now be a mid-chain member that
        // can't render the chain-wide focus indicator.
        if (!ok)
            restoreFocusToOrigin();
    };
    var moveFocus = function (delta, selector, searchRoot) {
        var _a;
        var btns = Array.from(searchRoot.querySelectorAll(selector));
        if (btns.length === 0)
            return;
        var active = document.activeElement;
        var curIdx = active ? btns.indexOf(active) : -1;
        var nextIdx = curIdx === -1
            ? (delta > 0 ? 0 : btns.length - 1)
            : (curIdx + delta + btns.length) % btns.length;
        (_a = btns[nextIdx]) === null || _a === void 0 ? void 0 : _a.focus();
    };
    var onContainerKeyDown = function (e) {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            if (subChoice) {
                setSubChoice(null);
            }
            else {
                closePicker();
                restoreFocusToOrigin();
            }
            return;
        }
        // Arrow keys + Tab navigate between items, both trapped within the picker
        // so focus can't escape to the rest of the page mid-flow. Sub-menu is
        // portaled to body, so we scope the lookup there; the main grid lives
        // inside containerRef.
        var selector = subChoice
            ? '.shape-context-menu--linkingCubePickerSub button.shape-context-menu__item'
            : 'button.linkingCubePicker__target';
        var searchRoot = subChoice
            ? document
            : containerRef.current;
        if (!searchRoot)
            return;
        if (e.key === 'ArrowRight' ||
            e.key === 'ArrowDown' ||
            (e.key === 'Tab' && !e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(1, selector, searchRoot);
        }
        else if (e.key === 'ArrowLeft' ||
            e.key === 'ArrowUp' ||
            (e.key === 'Tab' && e.shiftKey)) {
            e.preventDefault();
            e.stopPropagation();
            moveFocus(-1, selector, searchRoot);
        }
    };
    // Sub-prompt: user picked a standalone cube → choose above/below (vertical)
    // or left/right (horizontal). Rendered as a portal so it matches the real
    // context-menu styling and isn't distorted by the canvas zoom transform.
    // Side semantics are orientation-aware: for vertical, `'after'` = visually
    // above (smaller y); for horizontal, `'before'` = visually left (smaller x).
    if (subChoice && sub) {
        var targetBtn = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("button.linkingCubePicker__target[data-target-id=\"".concat(sub.cubeId, "\"]"));
        var rect = targetBtn === null || targetBtn === void 0 ? void 0 : targetBtn.getBoundingClientRect();
        var menuLeft = rect ? rect.right + 8 : 20;
        var menuTop = rect ? rect.top : 20;
        var isVertical = focusedCube.orientation === 'vertical';
        var primaryLabel = isVertical ? t('text.linkAbove') : t('text.linkLeft');
        var secondaryLabel = isVertical ? t('text.linkBelow') : t('text.linkRight');
        var primarySide_1 = isVertical ? 'after' : 'before';
        var secondarySide_1 = isVertical ? 'before' : 'after';
        return (_jsxs(_Fragment, { children: [_jsx("div", { ref: containerRef, className: "linkingCubePicker linkingCubePicker--sub", "aria-hidden": "true", children: candidates.map(function (cand) {
                        var _a, _b;
                        var visualSize = CSS_CUBE_SIZE * ((_a = cand.scale) !== null && _a !== void 0 ? _a : 1);
                        var positionSize = CUBE_SIZE * ((_b = cand.scale) !== null && _b !== void 0 ? _b : 1);
                        return (_jsx("div", { className: "linkingCubePicker__target linkingCubePicker__target--inactive", "data-target-id": cand.cubeId, "data-target-kind": cand.kind, style: {
                                position: 'absolute',
                                left: cand.center.x - positionSize / 2,
                                top: cand.center.y - positionSize / 2,
                                width: visualSize,
                                height: visualSize,
                                pointerEvents: 'none',
                            } }, "".concat(cand.cubeId, "-").concat(cand.kind)));
                    }) }), createPortal(_jsxs("div", { className: "shape-context-menu shape-context-menu--linkingCubePickerSub", role: "menu", "aria-label": t('text.linkingPickerSubLabel'), style: { left: menuLeft, top: menuTop }, onKeyDown: onContainerKeyDown, children: [_jsx("div", { className: "shape-context-menu__header", children: t('text.linkingPickerSubLabel') }), _jsx("button", { ref: subFirstBtnRef, type: "button", role: "menuitem", className: "shape-context-menu__item", onClick: function () { return commitLink(sub.cubeId, primarySide_1); }, children: _jsx("span", { className: "shape-context-menu__item-label", children: primaryLabel }) }), _jsx("button", { type: "button", role: "menuitem", className: "shape-context-menu__item", onClick: function () { return commitLink(sub.cubeId, secondarySide_1); }, children: _jsx("span", { className: "shape-context-menu__item-label", children: secondaryLabel }) }), _jsx("button", { type: "button", role: "menuitem", className: "shape-context-menu__item shape-context-menu__item--back", onClick: function () { return setSubChoice(null); }, children: _jsx("span", { className: "shape-context-menu__item-label", children: t('text.exitMenu') }) })] }), document.body)] }));
    }
    // Main picker: one invisible button positioned over each eligible target.
    return (_jsx("div", { ref: containerRef, className: "linkingCubePicker", role: "dialog", "aria-label": t('text.linkingPickerLabel'), onKeyDown: onContainerKeyDown, children: candidates.map(function (cand, idx) {
            var _a, _b;
            var visualSize = CSS_CUBE_SIZE * ((_a = cand.scale) !== null && _a !== void 0 ? _a : 1);
            var positionSize = CUBE_SIZE * ((_b = cand.scale) !== null && _b !== void 0 ? _b : 1);
            var left = cand.center.x - positionSize / 2;
            var top = cand.center.y - positionSize / 2;
            var onActivate = function () {
                var _a;
                if (cand.kind === 'standalone') {
                    setSubChoice({ targetId: cand.cubeId });
                }
                else {
                    commitLink(cand.cubeId, (_a = cand.side) !== null && _a !== void 0 ? _a : 'after');
                }
            };
            return (_jsx("button", { ref: idx === 0 ? firstBtnRef : undefined, type: "button", className: "linkingCubePicker__target", "data-target-id": cand.cubeId, "data-target-kind": cand.kind, style: {
                    position: 'absolute',
                    left: left,
                    top: top,
                    width: visualSize,
                    height: visualSize,
                    zIndex: 99999,
                }, onClick: onActivate, "aria-label": cand.label }, "".concat(cand.cubeId, "-").concat(cand.kind)));
        }) }));
};
export default LinkingCubePicker;
